import { MODERATION_VIOLATION_NOTICE_KEY, isDeliverableTemplateKey } from '@aysar/contracts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type FieldOption } from '@/shared/components/dynamic-form/types/dynamic-form.types';
import { useLookupNotificationCatalogueQuery } from '@/shared/services/notification-template-lookup/notification-template-lookup.api';

export interface TemplateKeyOptionsInput {
  /** The rule's own key, so its per-type notice can be offered. `''` while unset. */
  typeKey: string;
  /** What the field currently holds, so a saved value is always representable. */
  current: string;
}

/**
 * The keys a rule's legal notice may name — a list, where P20b had free text.
 *
 * Read through the **shared bridge** (W6): this module may never import
 * `modules/Notifications`, and the catalogue is the exact thing both need.
 *
 * ## Three sources, and the second one is the point
 *
 * 1. Every catalogue key, with whether anybody has written copy for it — because
 *    a key nobody has written is deliverable and silent, and the operator should
 *    see that before choosing it.
 * 2. **This rule's own `moderation.violation.<key>`**, which has no catalogue
 *    entry: it is a key FAMILY (P21a), resolved to the general enforcement
 *    notice until somebody authors copy for this specific rule. A picker that
 *    could not offer it would be a picker that refuses the most specific answer.
 * 3. Whatever the field already holds, when it is neither of the above. A select
 *    that cannot represent its own value silently blanks it on the next save.
 *
 * Deliberately **not creatable**: `isDeliverableTemplateKey` is what the contract
 * enforces, and every key that passes it is already on this list.
 */
export function useTemplateKeyOptions({
  typeKey,
  current,
}: TemplateKeyOptionsInput): FieldOption[] {
  const { t } = useTranslation('violations');
  const { data } = useLookupNotificationCatalogueQuery();

  return useMemo(() => {
    const options: FieldOption[] = (data ?? []).map((entry) => ({
      value: entry.key,
      label: entry.key,
      description:
        entry.authoredChannels.length === 0
          ? t('types.form.templateKeyUnauthored')
          : t('types.form.templateKeyAuthored'),
    }));

    const perType = typeKey === '' ? null : `moderation.violation.${typeKey}`;

    if (perType !== null && !options.some((option) => option.value === perType)) {
      // First, because it is the most specific answer and the one a rule usually
      // wants — the general notice is what it falls back to on its own.
      options.unshift({
        value: perType,
        label: perType,
        description: t('types.form.templateKeyPerType', {
          fallback: MODERATION_VIOLATION_NOTICE_KEY,
        }),
      });
    }

    if (current !== '' && !options.some((option) => option.value === current)) {
      options.push({
        value: current,
        label: current,
        // A stored value the catalogue does not know is either a family key this
        // client cannot resolve or a key from a newer api — said plainly rather
        // than dropped, which would blank the field on the next save.
        description: isDeliverableTemplateKey(current)
          ? t('types.form.templateKeyPerType', { fallback: MODERATION_VIOLATION_NOTICE_KEY })
          : t('types.form.templateKeyUnknown'),
      });
    }

    return options;
  }, [data, typeKey, current, t]);
}
